/* eslint-disable react/jsx-boolean-value */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable indent */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import useLoadSubInstances from '@folio/inventory/src/hooks/useLoadSubInstances';
import { Layout, Loading } from '@folio/stripes-components';
import { keyBy } from 'lodash';
import InstanceWizard from '../InstanceWizard';
import useInstance from '../../../hooks/useInstance';
import useHoldings from '../../../hooks/useHoldings';
import useItems from '../../../hooks/useItems';
import {
  UnParseClassifications,
  unParseHoldingsData,
  unParseItemsData,
} from '../../../utils/utils';
import OnSubmitProgressModal from './OnSubmitProgressModal';
import useControlledVocab from '../../../hooks/useDataQueries';
import useRecordLock from '../../../hooks/useRecordLock';
import useRecordLocks from '../../../hooks/RecordLocks/useRecordLocks';
import useRecordLockingSettings from '../../../hooks/useRecordLockingSettings';
import {
  RecordLockedWarningModal,
  InactivityWarningModal,
  ExitConfirmationModal,
} from '../../shared/RecordLocking';
import '../../shared/RecordLocking/ExitConfirmationModal/ExitConfirmationModal.css';

function EditInstance() {
  const { data, pending } = useControlledVocab();
  const { instanceId } = useParams();
  const history = useHistory();
  const intl = useIntl();
  const { isRecordLockingEnabled } = useRecordLockingSettings();

  // Form states
  const [formValues, setFormValues] = useState({});
  const [openSubmitProgress, setOpenSubmitProgress] = useState(false);

  // Record locking states
  const [showLockWarning, setShowLockWarning] = useState(false);
  const [showLockBanner, setShowLockBanner] = useState(false); // Only show for force edit
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const lockProcessedRef = useRef(false); // Prevent infinite loops
  const cleanupRef = useRef({
    isOwnLock: false,
    instanceId: null,
    unlockRecord: null,
  });

  // Inactivity callback for the lock hook
  const handleInactivityDetected = useCallback((_result) => {
    setShowInactivityWarning(true);
  }, []);

  // Record lock hook - only if record locking is enabled
  const {
    isLocked,
    isOwnLock,
    lockInfo,
    lockRecord,
    unlockRecord,
    updateActivity,
    checkSessionExtension,
  } = useRecordLock(
    isRecordLockingEnabled ? instanceId : null,
    handleInactivityDetected,
  );
  // Get loading state from useRecordLocks to know when we can start locking
  const { isLoading: isRecordLocksLoading, refetch: refetchRecordLocks } =
    useRecordLocks();

  const { instance, isInstanceLoading } = useInstance(instanceId);
  const { holdings, isHoldingsLoading } = useHoldings(instanceId);
  const { items, isItemsLoading } = useItems(holdings);

  const parentInstances = useLoadSubInstances(
    instance?.parentInstances,
    'superInstanceId',
  );
  const childInstances = useLoadSubInstances(
    instance?.childInstances,
    'subInstanceId',
  );

  const [templateId, setTemplateId] = useState('');

  const holdingSourceId = useMemo(() => {
    if (!pending) {
      return keyBy(data.holdingsSources, 'name').FOLIO?.id;
    }
    return '';
  }, [pending]);

  const defaultTemplate = useMemo(() => {
    if (!pending) {
      return data.templates.find((template) => template.isDefault === true);
    }
    return {};
  }, [pending]);

  const currentTemplate = useMemo(() => {
    if (!pending) {
      return data.templates.find((template) => template.id === templateId);
    }
    return {};
  }, [data.templates, pending, templateId]);

  const localizedTemplateName = useMemo(() => {
    if (!pending) {
      return currentTemplate?.name?.find(
        (field) => field?.locale === intl.locale,
      )?.value;
    }
    return '';
  }, [pending, templateId]);

  const initialValues = useMemo(() => {
    if (!pending) {
      return {
        instanceData: {
          ...instance,
          templateTypeId: currentTemplate?.id,
          classifications:
            instance?.customFields?.classifications ||
            UnParseClassifications(instance.classifications),
          childInstances,
          parentInstances,
        },
        holdingsData: unParseHoldingsData(holdings),
        itemsData: unParseItemsData(items),
      };
    }

    return {};
  }, [
    pending,
    isInstanceLoading,
    isHoldingsLoading,
    isItemsLoading,
    childInstances,
    parentInstances,
    templateId,
  ]);

  useEffect(() => {
    if (instance.templateTypeId) setTemplateId(instance.templateTypeId);
    else setTemplateId(defaultTemplate.id);
  }, [defaultTemplate.id, instance.templateTypeId]);

  // Check and handle record locking when component mounts
  useEffect(() => {
    if (!instanceId) return;

    // Reset lock processing flag when instanceId changes
    lockProcessedRef.current = false;

    // Force refresh lock data when opening a new instance
    refetchRecordLocks();
  }, [instanceId, refetchRecordLocks]);

  // Separate effect for lock handling to prevent infinite loops
  useEffect(() => {
    // Skip lock handling if record locking is disabled
    if (
      !isRecordLockingEnabled ||
      !instanceId ||
      lockProcessedRef.current ||
      isRecordLocksLoading
    ) {
      return;
    }

    if (isLocked && !isOwnLock) {
      // Record is locked by another user - show warning modal
      setShowLockWarning(true);
      lockProcessedRef.current = true;
    } else if (!isLocked && !lockProcessedRef.current) {
      // No lock exists - create new one
      lockProcessedRef.current = true; // Mark as processed before API call
      lockRecord()
        .then((result) => {
          if (!result.success) {
            lockProcessedRef.current = false; // Reset on error to retry
          }
        })
        .catch((error) => {
          lockProcessedRef.current = false; // Reset on error
        });
    } else if (isOwnLock) {
      lockProcessedRef.current = true;
    }
  }, [
    instanceId,
    isLocked,
    isOwnLock,
    lockRecord,
    isRecordLocksLoading,
    isRecordLockingEnabled,
  ]);

  // Handle lock warning modal actions
  const handleLockWarningCancel = useCallback(() => {
    setShowLockWarning(false);
    history.goBack();
  }, [history]);

  const handleForceEdit = useCallback(async () => {
    setShowLockWarning(false);
    setShowLockBanner(true); // Show warning banner - user editing without lock protection
  }, []);

  // Inactivity warning modal handlers
  const handleExtendSession = useCallback(async () => {
    try {
      const result = await checkSessionExtension(instance);
      if (result.success) {
        setShowInactivityWarning(false);
        // Reset activity tracking
        updateActivity();
      }
      return result;
    } catch (error) {
      return {
        success: false,
        error: 'حدث خطأ أثناء تمديد الجلسة',
        hasVersionConflict: false,
      };
    }
  }, [checkSessionExtension, instance, updateActivity]);

  const handleInactivityExit = useCallback(() => {
    setShowInactivityWarning(false);
    history.goBack();
  }, [history]);

  const handleViewLatestVersion = useCallback(() => {
    setShowInactivityWarning(false);
    // Reload the page to get the latest version
    window.location.reload();
  }, []);

  // Exit confirmation modal handlers
  const handleExitConfirm = useCallback(async () => {
    try {
      if (isOwnLock) {
        await unlockRecord();
      }
      setShowExitConfirmation(false);
      // Close the tab/window or navigate back
      if (window.history.length > 1) {
        history.goBack();
      } else {
        window.close();
      }
    } catch (error) {
      throw error;
    }
  }, [isOwnLock, unlockRecord, history]);

  const handleExitCancel = useCallback(() => {
    setShowExitConfirmation(false);
  }, []);

  // Update cleanup ref with current values
  useEffect(() => {
    cleanupRef.current = { isOwnLock, instanceId, unlockRecord };
  }, [isOwnLock, instanceId, unlockRecord]);

  // Cleanup: Release lock when component unmounts ONLY
  useEffect(() => {
    return () => {
      // Only release if record locking is enabled and we currently own a lock
      const {
        isOwnLock: currentIsOwnLock,
        instanceId: currentInstanceId,
        unlockRecord: currentUnlockRecord,
      } = cleanupRef.current;
      if (
        isRecordLockingEnabled &&
        currentIsOwnLock &&
        currentInstanceId &&
        currentUnlockRecord
      ) {
        currentUnlockRecord().catch(() => {});
      }
    };
  }, [isRecordLockingEnabled]); // Include isRecordLockingEnabled in dependencies

  const onSubmit = useCallback(
    async (values) => {
      formValues.holdingSourceId = holdingSourceId;
      setFormValues(values);

      setOpenSubmitProgress(true);
    },
    [formValues, instance],
  );

  // Handle successful submission - release lock only
  const handleSubmitSuccess = useCallback(async () => {
    if (isRecordLockingEnabled && isOwnLock) {
      try {
        await unlockRecord();
        setShowLockBanner(false);
      } catch (error) {
        // Failed to release lock after successful save
      }
    }
    // Keep modal open to show results - don't close here
  }, [isRecordLockingEnabled, isOwnLock, unlockRecord]);

  // Handle submission cancellation
  const handleSubmitCancel = useCallback(() => {
    setOpenSubmitProgress(false);
    // Keep the lock active when canceling submission
  }, []);

  // Handle browser beforeunload with custom modal
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isRecordLockingEnabled && isOwnLock) {
        // Prevent default browser dialog
        event.preventDefault();
        // Show our custom modal instead
        setShowExitConfirmation(true);
        // Return empty string to prevent browser dialog
        event.returnValue = '';
        return '';
      }
      // No return needed for else case in beforeunload
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isRecordLockingEnabled, isOwnLock]);

  // Track user activity for lock timeout
  const handleUserActivity = useCallback(() => {
    if (isRecordLockingEnabled && isOwnLock) {
      updateActivity();
    }
  }, [isRecordLockingEnabled, isOwnLock, updateActivity]);

  // Set up activity tracking - only for lock owners
  useEffect(() => {
    // Only track activity if record locking is enabled and user owns the lock
    if (!isRecordLockingEnabled || !isOwnLock) return;

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'focus',
    ];

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [isRecordLockingEnabled, isOwnLock, handleUserActivity]);

  return (
    <>
      {/* Record Locked Warning Modal - Only show if record locking is enabled */}
      {isRecordLockingEnabled && showLockWarning && (
        <RecordLockedWarningModal
          isOpen={showLockWarning}
          recordType="instance"
          lockedBy={lockInfo?.userDisplayName || 'مستخدم غير معروف'}
          duration={lockInfo?.duration || 0}
          onCancel={handleLockWarningCancel}
          onForceEdit={handleForceEdit}
          allowForceEdit={true}
        />
      )}

      {/* Inactivity Warning Modal - Only show if record locking is enabled */}
      {isRecordLockingEnabled && showInactivityWarning && (
        <InactivityWarningModal
          isOpen={showInactivityWarning}
          onExtendSession={handleExtendSession}
          onExit={handleInactivityExit}
          onViewLatestVersion={handleViewLatestVersion}
        />
      )}

      {/* Exit Confirmation Modal - Only show if record locking is enabled */}
      {isRecordLockingEnabled && showExitConfirmation && (
        <ExitConfirmationModal
          isOpen={showExitConfirmation}
          onConfirm={handleExitConfirm}
          onCancel={handleExitCancel}
        />
      )}

      {/* Submit Progress Modal */}
      <OnSubmitProgressModal
        open={openSubmitProgress}
        onClose={handleSubmitCancel}
        onSuccess={handleSubmitSuccess}
        formValues={formValues}
        initialValues={initialValues}
        instance={instance}
        instanceId={instanceId}
        holdings={holdings}
        items={items}
      />
      {!pending && (
        <InstanceWizard
          actionType="edit"
          appIcon="edit"
          currentTemplate={currentTemplate || defaultTemplate}
          initialValues={initialValues}
          controlledVocab={data}
          isLoading={
            pending || isInstanceLoading || isHoldingsLoading || isItemsLoading
          }
          onSubmit={onSubmit}
          setTemplateId={setTemplateId}
          originTemplateId={instance.templateTypeId || defaultTemplate?.id}
          onUserActivity={handleUserActivity}
          showLockBanner={isRecordLockingEnabled && showLockBanner}
          lockInfo={lockInfo}
          paneTitle={
            pending ||
            isInstanceLoading ||
            isHoldingsLoading ||
            isItemsLoading ? (
              <Loading />
            ) : (
              <FormattedMessage
                id="ui-plugin-inventory-templates.instanceWizard.edit.pane.paneTitle"
                values={{
                  title:
                    instance?.title?.length > 150
                      ? instance?.title?.slice(0, 150) + '...'
                      : instance?.title,
                  template: localizedTemplateName,
                }}
                defaultMessage="Edit ({title}) instance ({template})"
              />
            )
          }
        />
      )}
      {pending && (
        <Layout
          className="centered full"
          style={{ maxWidth: '15rem', height: '8rem' }}
        >
          &nbsp;
          <Loading size="xlarge" />
        </Layout>
      )}
    </>
  );
}

EditInstance.propTypes = {};

export default EditInstance;
